/*
 * StreamBank Admin v38.0
 * Copyright © 2017-Present, The Freshwater Trust, all rights reserved.
 */

import React, {Fragment, useEffect, useState} from 'react';

import {useMakePageRequest} from "../../../Hooks/RequestHooks";
import {service as OfflineDataService} from "../../../Services/OfflineDataService";
import {useSelector} from "react-redux";
import {StreamBankPageHeader} from "../../Common/StreamBankPageHeader";
import {Col, Container, FormControl, Row} from "react-bootstrap";
import {useClassNames} from "../../../Hooks/StyleHooks";
import {config} from "../../../Config";
import {ImageCompareSlider} from './ImageCompareSlider';
import {usePatchQueryParams, useQueryParams} from "../../../../../monitoring/src/Hooks/RouteHooks"

import {SampleEventPicker} from "./SampleEventPicker";
import {PhotoDescription} from "./PhotoDescription";

export const PhotoComparisonPage = props => {

    const patchQueryParams = usePatchQueryParams();

    const getPageName = () => {
        return `sb-photos-index-page`;
    };

    const noPhotoAvailable = {
        src: "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
    }

    const makePageRequest = useMakePageRequest();
    const classNames = useClassNames(getPageName());

    const context = useSelector(state => state.contextState.context);
    const [photoPoints, setPhotoPoints] = useState([]);
    const [currentPhotoPoint, setCurrentPhotoPoint] = useState(null);
    const [photo1, setPhoto1] = useState(null);
    const [photo2, setPhoto2] = useState(null);
    const [lastChanged, setLastChanged] = useState(0);
    const [leftImageLoaded, setLeftImageLoaded] = useState(false);
    const [rightImageLoaded, setRightImageLoaded] = useState(false);

    const firstPhotoPointId = (photoPoints[0]?.photoPointId);
    const firstSampleEventId = currentPhotoPoint?.sampleEvents?.[0]?.[0];

    const {
        photoPointId = firstPhotoPointId,
        leftSampleEventId = firstSampleEventId,
        rightSampleEventId = null
    } = useQueryParams();

    useEffect(() => {
        setLastChanged(0);
        setCurrentPhotoPoint(photoPoints.find(photoPoint => (
            Number(photoPoint.photoPointId) === Number(photoPointId)
        )))
    }, [photoPoints, photoPointId]);

    useEffect(() => {
        if (context.contextNodeId && context.contextNodeTypeId === 3) {
            makePageRequest(
                _ => OfflineDataService.Download(context.contextNodeId, null, null, true),
                ({CameraPoints, Photos}) => {
                    let availablePhotoPoints = [];
                    const availableSampleEvents = Array.from(
                        Photos.filter((value, index, self) => {
                            return self.findIndex(v => v.sampleEventId === value.sampleEventId) === index;
                        })
                        .map(p => [p.sampleEventId, p.sampleEventName])
                    );

                    CameraPoints.forEach(cameraPoint => {
                        cameraPoint.PhotoPoints.forEach(photoPoint => {
                            const photos = Photos
                                .filter(photo => Number(photo.photoPointId) === Number(photoPoint.photoPointId))
                                .map(photo => ({
                                    ...photo,
                                    src: getPhotoSrc(photo)
                                }));

                            if (photos.length) {
                                const sampleEvents = availableSampleEvents.map(
                                    ([sampleEventId, sampleEventName]) => {
                                        const hasPhoto = (photos.find(photo => photo.sampleEventId === sampleEventId));
                                        return [sampleEventId, sampleEventName + (hasPhoto ? '' : ' (No Photo)'), !hasPhoto];
                                    }
                                );

                                availablePhotoPoints = [...availablePhotoPoints, {
                                    ...photoPoint,
                                    cameraPoint: {...cameraPoint, PhotoPoints: undefined},
                                    name: `${cameraPoint.name}${photoPoint.name}`,
                                    photos,
                                    sampleEvents,
                                }]
                            }
                        })
                    });
                    setPhotoPoints(availablePhotoPoints);
                }
            );
        }
    }, [context.contextNodeId]);

    useEffect(() => {
        setLastChanged(1);
        if (currentPhotoPoint && leftSampleEventId) {
            const photo = currentPhotoPoint.photos.find(photo => Number(photo.sampleEventId) === Number(leftSampleEventId));
            setPhoto1(photo ?? noPhotoAvailable);
        } else {
            setPhoto1(null);
        }
    }, [leftSampleEventId, currentPhotoPoint]);

    useEffect(() => {
        setLastChanged(2);
        if (currentPhotoPoint && rightSampleEventId) {
            const photo = currentPhotoPoint.photos.find(photo => Number(photo.sampleEventId) === Number(rightSampleEventId));
            setPhoto2(photo ?? noPhotoAvailable);
        } else {
            setPhoto2(null);
        }
    }, [rightSampleEventId, currentPhotoPoint]);

    useEffect(() => {
        setLeftImageLoaded(false);
    }, [photo1]);

    useEffect(() => {
        setRightImageLoaded(false);
    }, [photo2]);

    const getPhotoSrc = (photo) => {
        if(photo) {
            return config.getApiUrl() + "/api/photos/" + photo.photoId + "/large"
        } else {
            console.log(photo);
        }
        return "";
    };

    const getLoadingComponent = () => {
        const imgElement = (photo1 ?? photo2) ? <img src={getPhotoSrc(photo1 ?? photo2)}/> : null;
        const colorClass = imgElement ? "white" : "black";
        return <Fragment>
            <div className={`overlay-message ${colorClass}`}>
                Loading...
            </div>
        </Fragment>
    }

    const queryParamOnChangeHandler = (queryParamName, currentValue) => (x) => {
        const newValue = x.target.value;
        if(Number(newValue) !== Number(currentValue)) {
            patchQueryParams({[queryParamName]: newValue});
        }
    }

    return <Container className={classNames}>
        <div>
            <StreamBankPageHeader Title="Photos"/>
            <Row>
                <Col xs={9}><h2>Photo Comparisons</h2></Col>
                <Col xs={3}>
                    <FormControl
                        as="select"
                        placeholder="Select Photo Point"
                        value={currentPhotoPoint?.photoPointId ?? ''}
                        onChange={queryParamOnChangeHandler('photoPointId', photoPointId)}
                    >
                        {
                            photoPoints?.map(photoPoint => (
                                <option value={photoPoint.photoPointId} key={photoPoint.photoPointId}>Photo Point {photoPoint.name}</option>
                            )) ?? null
                        }
                    </FormControl>
                </Col>
            </Row>
            <div>
                <hr className="special-hr"/>
            </div>
            <Row>
                <Col xs={12}>
                    <div className="photo-container">
                        <div className="photo">
                            {
                                (photo1?.photoId && photo2?.photoId) ?
                                    <ImageCompareSlider leftImage={photo1?.src} rightImage={photo2?.src} skeleton={getLoadingComponent()} /> :
                                    null
                            }
                            {
                                <div className={`overlay-message black`}>
                                    {
                                        !photoPoints.length ?
                                            "This project has no photo points" :
                                        (!leftImageLoaded && !rightImageLoaded) ?
                                            "Loading..." :
                                        null
                                    }
                                </div>
                            }
                            {
                                lastChanged && photo1?.src ? <img className={lastChanged === 1 ? "bottom" : "top"} src={photo1?.src} onLoad={() => setLeftImageLoaded(true)} /> : null
                            }
                            {
                                lastChanged && photo2?.src ? <img className={lastChanged === 2 ? "bottom" : "top"} src={photo2?.src} onLoad={() => setRightImageLoaded(true)} /> : null
                            }
                            {
                                (leftImageLoaded && rightImageLoaded && !photo1?.photoId && !photo2?.photoId) &&
                                <div className={`overlay-message black`}>No Photo(s) Selected</div>
                            }
                        </div>
                    </div>
                </Col>
                <Col xs={12}>
                    <div className="sb-photo-point-data">
                        <div>
                            <PhotoDescription photo={photo1}/>
                            <SampleEventPicker
                                value={leftSampleEventId ?? ''}
                                onChange={queryParamOnChangeHandler('leftSampleEventId', leftSampleEventId)}
                                sampleEvents={currentPhotoPoint?.sampleEvents}
                                emptyValue={!photoPoints.length ? "No Photos" : !currentPhotoPoint ? "Loading..." : null}
                            />
                        </div>
                        <div>
                            <div className="sb-photo-point-details">
                                {
                                    !!currentPhotoPoint &&
                                    <Fragment>
                                        <span>{currentPhotoPoint?.cameraPoint?.latitude}, {currentPhotoPoint?.cameraPoint?.longitude}</span>
                                        <span>{currentPhotoPoint?.photoDirection} {currentPhotoPoint?.compassBearing}&#176;</span>
                                        <span>Bank: {currentPhotoPoint?.cameraPoint?.bank}</span>
                                    </Fragment>
                                }
                            </div>
                        </div>
                        <div>
                            <PhotoDescription photo={photo2}/>
                            <SampleEventPicker
                                value={rightSampleEventId ?? ''}
                                onChange={queryParamOnChangeHandler('rightSampleEventId', rightSampleEventId)}
                                sampleEvents={currentPhotoPoint?.sampleEvents}
                                emptyValue={!photoPoints.length ? "No Photos" :"Select Comparison Year"}
                            />
                        </div>
                    </div>
                </Col>
            </Row>
        </div>
    </Container>;
};


